/**
 * Estado do formulário do protocolo de assinatura (E74.F1) e as conversões
 * puras de ida e volta com o contrato do servidor. Sem JSX, sem hooks.
 *
 * O formulário guarda tudo como string (input controlado); a conversão para o
 * contrato faz vazio → null, número → number e datetime-local → ISO. O caminho
 * inverso hidrata o formulário a partir do protocolo salvo.
 */
import {
  biometriaDoDossie,
  type BiometriaDoDossie,
} from '@/components/ExameEletronico/protocolo_form_biometria';
import { ptBR } from '@/i18n/pt-BR';
import type {
  EnvelopeDoProtocoloData,
  EventoProtocoloData,
  FonteCoordenada,
  FormatoDaData,
  LidoDosDocumentosData,
  PapelIp,
  ProtocoloAssinaturaData,
  TipoAssinatura,
  TipoEventoProtocolo,
} from '@/lib/exame_eletronico';
import { formatCpf } from '@/lib/masks';

export type ProvaDeVidaForm = 'nao_informado' | 'sim' | 'nao';

export interface EventoForm {
  tipo: TipoEventoProtocolo;
  emUtc: string;
  emLocal: string;
  descricao: string;
}

export interface IpForm {
  endereco: string;
  papel: PapelIp;
}

/**
 * E98.F2 — o que a leitura dos comprovantes grava e a tela não edita: o envelope por
 * papel e a coleta declarada pelo provedor da biometria. Volta intacto no "Salvar",
 * porque o servidor substitui o protocolo inteiro (a lição da E84.F9).
 */
export interface LidoDoComprovante {
  envelope: EnvelopeDoProtocoloData | null;
  moduloDeColeta: string | null;
  coletaPorReaproveitamento: boolean | null;
  documentoValidado: boolean | null;
  /** E101.F3 — a biometria do dossiê em imagem que a tela não edita. */
  biometriaDoDossie: BiometriaDoDossie;
  /** E106.F2 — o que o arquivo imprime (coordenada parcial, laudo, carimbos). */
  lidoDosDocumentos: LidoDosDocumentosData | null;
}

export interface ProtocoloFormState {
  plataforma: string;
  codigoVerificacao: string;
  /** E93.F1 — o endereço de validação impresso no comprovante. */
  urlVerificacao: string;
  proposta: string;
  envelopeId: string;
  assinaturaId: string;
  hashDeclarado: string;
  tipoAssinatura: TipoAssinatura;
  /** E93.F1 — a frase literal do tipo, como o comprovante a imprime. */
  tipoAssinaturaTexto: string;
  signatarioNome: string;
  signatarioCpf: string;
  signatarioEmail: string;
  signatarioTelefone: string;
  eventos: EventoForm[];
  /** E84.F4 — `datetime-local` em UTC, como os eventos. */
  assinaturaRegistradaEm: string;
  assinaturaRegistradaEmFusoPresumido: boolean;
  /** E114.F1 (R-20) — a linha de status imprime só hora e minuto; editar o registro a desfaz. */
  assinaturaRegistradaEmSemSegundos: boolean;
  /**
   * E84.F9 (GUS-44 RF01/CA02): o formato declarado da data. `''` = não
   * declarado, que é um estado legítimo — a leitura literal fica e a
   * ambiguidade sai pendente no quadro.
   */
  formatoDaData: FormatoDaData | '';
  ips: IpForm[];
  latitude: string;
  longitude: string;
  precisaoM: string;
  fonteCoordenada: FonteCoordenada;
  userAgent: string;
  fingerprint: string;
  biometriaPresente: boolean;
  biometriaSessaoId: string;
  biometriaScore: string;
  biometriaThreshold: string;
  biometriaProvaDeVida: ProvaDeVidaForm;
  biometriaFornecedor: string;
  lidoDoComprovante: LidoDoComprovante;
  observacoes: string;
}

export interface ProtocoloFormErros {
  hashDeclarado?: string;
  urlVerificacao?: string;
  eventos?: string;
  coordenada?: string;
}

const SHA256_HEX = /^[0-9a-f]{64}$/;
/** Um rótulo de nome de host: começa e termina em letra ou dígito. */
const ROTULO_DE_HOST = /^[\p{L}\p{N}](?:[\p{L}\p{N}_-]*[\p{L}\p{N}])?$/u;

/**
 * A régua do contrato (E93.F1; F-E93.F1-02): endereço web inteiro, sem espaço, com host de
 * verdade — "https://@", "http://:80" e "https://." não têm endereço nenhum.
 */
export function urlDeVerificacaoValida(url: string): boolean {
  if (/\s/.test(url) || !/^https?:\/\//i.test(url)) return false;
  let host: string;
  try {
    host = new URL(url).hostname;
  } catch {
    return false;
  }
  if (host.startsWith('[') && host.endsWith(']')) return host.length > 2;
  const rotulos = host.replace(/\.$/, '').split('.');
  return host.length > 0 && rotulos.every((r) => ROTULO_DE_HOST.test(r));
}
const DATETIME_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

export function eventoVazio(): EventoForm {
  return { tipo: 'assinatura', emUtc: '', emLocal: '', descricao: '' };
}

export function ipVazio(): IpForm {
  return { endereco: '', papel: 'cliente' };
}

export function estadoVazio(): ProtocoloFormState {
  return {
    plataforma: '',
    codigoVerificacao: '',
    urlVerificacao: '',
    proposta: '',
    envelopeId: '',
    assinaturaId: '',
    hashDeclarado: '',
    tipoAssinatura: 'nao_informado',
    tipoAssinaturaTexto: '',
    signatarioNome: '',
    signatarioCpf: '',
    signatarioEmail: '',
    signatarioTelefone: '',
    eventos: [],
    assinaturaRegistradaEm: '',
    assinaturaRegistradaEmFusoPresumido: false,
    assinaturaRegistradaEmSemSegundos: false,
    formatoDaData: '',
    ips: [],
    latitude: '',
    longitude: '',
    precisaoM: '',
    fonteCoordenada: 'nao_informada',
    userAgent: '',
    fingerprint: '',
    biometriaPresente: false,
    biometriaSessaoId: '',
    biometriaScore: '',
    biometriaThreshold: '',
    biometriaProvaDeVida: 'nao_informado',
    biometriaFornecedor: '',
    lidoDoComprovante: {
      envelope: null,
      moduloDeColeta: null,
      coletaPorReaproveitamento: null,
      documentoValidado: null,
      biometriaDoDossie: {},
      lidoDosDocumentos: null,
    },
    observacoes: '',
  };
}

// Datas -------------------------------------------------------------------

function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

/** ISO (qualquer fuso) → valor de `datetime-local` lido em UTC. */
export function datetimeLocalDeIsoUtc(iso: string | null | undefined): string {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toISOString().slice(0, 16);
}

/** Valor de `datetime-local` digitado COMO UTC → ISO com `Z`. */
export function isoUtcDeDatetimeLocal(valor: string): string | null {
  const v = valor.trim();
  if (!DATETIME_LOCAL.test(v)) return null;
  return v.length === 16 ? `${v}:00Z` : `${v}Z`;
}

/** ISO (qualquer fuso) → valor de `datetime-local` no fuso deste navegador. */
export function datetimeLocalDeIsoLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return (
    `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}` +
    `T${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`
  );
}

/**
 * Valor de `datetime-local` (horário local do protocolo) → ISO com o deslocamento
 * do fuso deste navegador. É a melhor aproximação disponível: o protocolo
 * imprime o horário local sem dizer o fuso, e o escritório está no mesmo fuso
 * do signatário na quase totalidade dos casos.
 */
export function isoLocalDeDatetimeLocal(valor: string): string | null {
  const v = valor.trim();
  if (!DATETIME_LOCAL.test(v)) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const offsetMin = -d.getTimezoneOffset();
  const sinal = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const offset = `${sinal}${doisDigitos(Math.floor(abs / 60))}:${doisDigitos(abs % 60)}`;
  return `${v.length === 16 ? `${v}:00` : v}${offset}`;
}

// Normalizações -----------------------------------------------------------

/** Mesma normalização do servidor: tira espaços/dois-pontos e baixa a caixa. */
export function normalizarHash(valor: string): string {
  return valor.replace(/[\s:]/g, '').toLowerCase();
}

function textoOuNull(valor: string): string | null {
  const v = valor.trim();
  return v.length > 0 ? v : null;
}

function numeroOuNull(valor: string): number | null {
  const v = valor.trim().replace(',', '.');
  if (v.length === 0) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function provaDeVida(valor: ProvaDeVidaForm): boolean | null {
  if (valor === 'sim') return true;
  if (valor === 'nao') return false;
  return null;
}

function provaDeVidaForm(valor: boolean | null | undefined): ProvaDeVidaForm {
  if (valor === true) return 'sim';
  if (valor === false) return 'nao';
  return 'nao_informado';
}

// Ida e volta -------------------------------------------------------------

function texto(valor: string | null | undefined): string {
  return valor ?? '';
}

function numeroTexto(valor: number | null | undefined): string {
  return valor == null ? '' : String(valor);
}

function identificacaoForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  return {
    plataforma: texto(p.plataforma),
    codigoVerificacao: texto(p.codigo_verificacao),
    urlVerificacao: texto(p.url_verificacao),
    proposta: texto(p.proposta),
    envelopeId: texto(p.envelope_id),
    assinaturaId: texto(p.assinatura_id),
    hashDeclarado: texto(p.hash_declarado),
    tipoAssinatura: p.tipo_assinatura,
    tipoAssinaturaTexto: texto(p.tipo_assinatura_texto),
    formatoDaData: p.formato_da_data ?? '',
    observacoes: texto(p.observacoes),
  };
}

function signatarioForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  return {
    signatarioNome: texto(p.signatario.nome),
    signatarioCpf: formatCpf(texto(p.signatario.cpf)),
    signatarioEmail: texto(p.signatario.email),
    signatarioTelefone: texto(p.signatario.telefone),
  };
}

function listasForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  return {
    eventos: p.eventos.map((e) => ({
      tipo: e.tipo,
      emUtc: datetimeLocalDeIsoUtc(e.em_utc),
      emLocal: datetimeLocalDeIsoLocal(e.em_local),
      descricao: texto(e.descricao),
    })),
    ips: p.ips.map((ip) => ({ endereco: ip.endereco, papel: ip.papel })),
    assinaturaRegistradaEm: datetimeLocalDeIsoUtc(p.assinatura_registrada_em),
    assinaturaRegistradaEmFusoPresumido: p.assinatura_registrada_em_fuso_presumido === true,
    assinaturaRegistradaEmSemSegundos: p.assinatura_registrada_em_sem_segundos === true,
  };
}

function coordenadaForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  const c = p.coordenada;
  if (c === null) return {};
  return {
    latitude: String(c.latitude),
    longitude: String(c.longitude),
    precisaoM: numeroTexto(c.precisao_m),
    fonteCoordenada: c.fonte,
  };
}

function dispositivoForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  if (p.dispositivo === null) return {};
  return {
    userAgent: texto(p.dispositivo.user_agent),
    fingerprint: texto(p.dispositivo.fingerprint),
  };
}

function biometriaForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  const b = p.biometria;
  return {
    biometriaPresente: b.presente,
    biometriaSessaoId: texto(b.sessao_id),
    biometriaScore: numeroTexto(b.score),
    biometriaThreshold: numeroTexto(b.threshold),
    biometriaProvaDeVida: provaDeVidaForm(b.prova_de_vida),
    biometriaFornecedor: texto(b.fornecedor),
    lidoDoComprovante: {
      envelope: p.envelope ?? null,
      moduloDeColeta: b.modulo_de_coleta ?? null,
      coletaPorReaproveitamento: b.coleta_por_reaproveitamento ?? null,
      documentoValidado: b.documento_validado ?? null,
      biometriaDoDossie: biometriaDoDossie(b),
      lidoDosDocumentos: p.lido_dos_documentos ?? null,
    },
  };
}

export function estadoDoProtocolo(p: ProtocoloAssinaturaData | null): ProtocoloFormState {
  if (p === null) return estadoVazio();
  return {
    ...estadoVazio(),
    ...identificacaoForm(p),
    ...signatarioForm(p),
    ...listasForm(p),
    ...coordenadaForm(p),
    ...dispositivoForm(p),
    ...biometriaForm(p),
  };
}

function eventoTemHorario(e: EventoForm): boolean {
  return e.emUtc.trim().length > 0 || e.emLocal.trim().length > 0;
}

/** Validação do que o servidor recusaria com 422 — antes de enviar. */
export function validarProtocoloForm(s: ProtocoloFormState): ProtocoloFormErros {
  const t = ptBR.exameEletronico.form;
  const erros: ProtocoloFormErros = {};
  const hash = normalizarHash(s.hashDeclarado);
  if (hash.length > 0 && !SHA256_HEX.test(hash)) erros.hashDeclarado = t.hashDeclaradoErro;
  const url = s.urlVerificacao.trim();
  if (url.length > 0 && !urlDeVerificacaoValida(url)) erros.urlVerificacao = t.urlVerificacaoErro;
  if (s.eventos.some((e) => !eventoTemHorario(e))) erros.eventos = t.eventoSemHorario;
  const temLat = s.latitude.trim().length > 0;
  const temLong = s.longitude.trim().length > 0;
  if (temLat !== temLong) erros.coordenada = t.coordenadaIncompleta;
  return erros;
}

function eventoDoForm(e: EventoForm): EventoProtocoloData {
  return {
    tipo: e.tipo,
    em_utc: isoUtcDeDatetimeLocal(e.emUtc),
    em_local: isoLocalDeDatetimeLocal(e.emLocal),
    descricao: textoOuNull(e.descricao),
  };
}

function coordenadaDoForm(s: ProtocoloFormState): ProtocoloAssinaturaData['coordenada'] {
  const latitude = numeroOuNull(s.latitude);
  const longitude = numeroOuNull(s.longitude);
  if (latitude === null || longitude === null) return null;
  return { latitude, longitude, precisao_m: numeroOuNull(s.precisaoM), fonte: s.fonteCoordenada };
}

function dispositivoDoForm(s: ProtocoloFormState): ProtocoloAssinaturaData['dispositivo'] {
  const user_agent = textoOuNull(s.userAgent);
  const fingerprint = textoOuNull(s.fingerprint);
  if (user_agent === null && fingerprint === null) return null;
  return { user_agent, fingerprint };
}

export function protocoloDoEstado(s: ProtocoloFormState): ProtocoloAssinaturaData {
  const hash = normalizarHash(s.hashDeclarado);
  return {
    plataforma: textoOuNull(s.plataforma),
    codigo_verificacao: textoOuNull(s.codigoVerificacao),
    url_verificacao: textoOuNull(s.urlVerificacao),
    proposta: textoOuNull(s.proposta),
    envelope_id: textoOuNull(s.envelopeId),
    assinatura_id: textoOuNull(s.assinaturaId),
    hash_declarado: hash.length > 0 ? hash : null,
    tipo_assinatura: s.tipoAssinatura,
    tipo_assinatura_texto: textoOuNull(s.tipoAssinaturaTexto),
    signatario: {
      nome: textoOuNull(s.signatarioNome),
      cpf: textoOuNull(s.signatarioCpf),
      email: textoOuNull(s.signatarioEmail),
      telefone: textoOuNull(s.signatarioTelefone),
    },
    eventos: s.eventos.map(eventoDoForm),
    assinatura_registrada_em: isoUtcDeDatetimeLocal(s.assinaturaRegistradaEm),
    assinatura_registrada_em_fuso_presumido: s.assinaturaRegistradaEmFusoPresumido,
    assinatura_registrada_em_sem_segundos: s.assinaturaRegistradaEmSemSegundos,
    // E84.F9: sem esta linha, todo "Salvar" apagava o formato declarado — o
    // corpo ia sem a chave, o servidor assumia ausente e o `upsert` substituía.
    formato_da_data: s.formatoDaData === '' ? null : s.formatoDaData,
    ips: s.ips
      .filter((ip) => ip.endereco.trim().length > 0)
      .map((ip) => ({ endereco: ip.endereco.trim(), papel: ip.papel })),
    coordenada: coordenadaDoForm(s),
    dispositivo: dispositivoDoForm(s),
    biometria: {
      presente: s.biometriaPresente,
      sessao_id: textoOuNull(s.biometriaSessaoId),
      score: numeroOuNull(s.biometriaScore),
      threshold: numeroOuNull(s.biometriaThreshold),
      prova_de_vida: provaDeVida(s.biometriaProvaDeVida),
      fornecedor: textoOuNull(s.biometriaFornecedor),
      modulo_de_coleta: s.lidoDoComprovante.moduloDeColeta,
      coleta_por_reaproveitamento: s.lidoDoComprovante.coletaPorReaproveitamento,
      documento_validado: s.lidoDoComprovante.documentoValidado,
      ...s.lidoDoComprovante.biometriaDoDossie,
    },
    envelope: s.lidoDoComprovante.envelope,
    lido_dos_documentos: s.lidoDoComprovante.lidoDosDocumentos,
    observacoes: textoOuNull(s.observacoes),
  };
}
